import React, { useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { analytics, dateFnsFormats, gridStickyCtaCMSContent, membershipLens, RENTAL_INTENT } from "rtr-constants";
import ActionLogger from "action-logger";
import AtomStickyPrimaryButton from "../atoms/atom-sticky-primary-button";
import { loadCMSContent } from "actions/shared-actions";
import { openDateFilterModal } from "actions/grid-modal-actions";
import tryCatchLog from "helpers/try-catch";
import { navigateTo } from "helpers/location-helpers";
import { setMembershipLens } from "actions/products-actions";
import { addDays, format, parseISO } from "date-fns";
import { getRentalIntent } from "helpers/rental-intent-helper";
import { isActiveMembership } from "helpers/membership-helpers";

const GridStickyCta = ({ content, currentIntent, isActiveMembership, isRentableGrid, isVisible, workingFilters }) => {
  const dispatch = useDispatch();

  const triggerDateFilterModal = () => dispatch(openDateFilterModal());
  const setLens = lens => dispatch(setMembershipLens(lens));

  useEffect(() => {
    //Subscribers don't see this element so no need to load the content for them
    if (!content && !isActiveMembership) {
      const { CMS_PATH, STATE_KEY } = gridStickyCtaCMSContent;
      dispatch(loadCMSContent(CMS_PATH, STATE_KEY));
    }
  }, [isActiveMembership]);

  const getStickyCtaProps = () => {
    const { primary = {}, secondary = {} } = content[currentIntent];

    let buttonText = primary.ctaText;

    //If the primary button is a date select and there's no explicit copy, show generic copy.
    if (primary.ctaAction === GridStickyCta.ctaActions.selectDates && !buttonText) {
      buttonText = getDateSelectCopy();
    }

    return {
      buttonText: buttonText,
      buttonOnClick: getOnClick(primary.ctaUrlPath, primary.ctaAction, primary.ctaAnalyticsIntent),
      bottomButtonText: secondary.ctaText,
      bottomButtonOnClick: getOnClick(secondary.ctaUrlPath, secondary.ctaAction, secondary.ctaAnalyticsIntent),
    };
  };

  const getOnClick = (urlPath, ctaAction, ctaAnalyticsIntent) => {
    if (urlPath) {
      return () => onClickNavigate(urlPath);
    }

    const { ctaActions } = GridStickyCta;

    switch (ctaAction) {
      case ctaActions.setMembershipIntent:
        return () => onClickSetIntent(membershipLens.unlimited, ctaAnalyticsIntent);
      case ctaActions.setReserveIntent:
        return () => onClickSetIntent(membershipLens.classic, ctaAnalyticsIntent);
      case ctaActions.selectDates:
        return onClickSelectDate;
      default:
        return () => {};
    }
  };

  const getDateSelectCopy = () => {
    const { date, duration } = workingFilters;

    if (!date || !duration) {
      return "Choose Your Rental Dates";
    }

    const parsedStart = parseISO(date);
    const start = format(parsedStart, dateFnsFormats.MM_DD);
    const parsedEnd = addDays(parsedStart, duration - 1);
    const end = format(parsedEnd, dateFnsFormats.MM_DD);

    return `Rental Dates: ${start} - ${end}`;
  };

  const getAnalyticsAction = ctaAnalyticsIntent => {
    return ctaAnalyticsIntent === RENTAL_INTENT.MEMBERSHIP
      ? analytics.ACTION_TYPE.CLICK_TO_SUBSCRIPTION
      : analytics.ACTION_TYPE.CLICK_TO_RESERVE_INTENT;
  };

  const logClickDateSelect = () => {
    ActionLogger.logAction({
      object_type: analytics.OBJECT_TYPE.STICKY_CTA,
      action: analytics.ACTION_TYPE.CLICK_TO_DATE_SELECT,
      current_intent: currentIntent,
    });
  };

  const logNavigate = ctaAnalyticsIntent => {
    ActionLogger.inferAction({
      object_type: analytics.OBJECT_TYPE.STICKY_CTA,
      action: getAnalyticsAction(ctaAnalyticsIntent),
      current_intent: currentIntent,
    });
  };

  const logChangeIntent = ctaAnalyticsIntent => {
    ActionLogger.logAction({
      object_type: analytics.OBJECT_TYPE.STICKY_CTA,
      action: getAnalyticsAction(ctaAnalyticsIntent),
      current_intent: currentIntent,
    });
  };

  const onClickSelectDate = () => {
    logClickDateSelect();
    triggerDateFilterModal();
  };

  const onClickNavigate = (path, ctaAnalyticsIntent) => {
    logNavigate(ctaAnalyticsIntent);
    tryCatchLog(() => navigateTo(path));
  };

  const onClickSetIntent = (lens, ctaAnalyticsIntent) => {
    logChangeIntent(ctaAnalyticsIntent);
    setLens(lens);
  };

  const render = () => {
    if (!content?.[currentIntent] || !isRentableGrid || isActiveMembership) {
      return null;
    }

    const { buttonText, bottomButtonText, buttonOnClick, bottomButtonOnClick } = getStickyCtaProps();

    return (
      <AtomStickyPrimaryButton
        buttonText={buttonText}
        bottomButtonText={bottomButtonText}
        buttonOnClick={buttonOnClick}
        bottomButtonOnClick={bottomButtonOnClick}
        dataTestId={"grid-sticky-cta"}
        isVisible={isVisible}
      />
    );
  };

  return render();
};

const mapStateToProps = state => ({
  content: state.cmsContent?.[gridStickyCtaCMSContent.STATE_KEY],
  currentIntent: getRentalIntent(state.userData),
  isActiveMembership: isActiveMembership(state.membershipState),
  isRentableGrid: state.gridView?.isRentable,
  workingFilters: state.workingFilters,
});

GridStickyCta.ctaActions = {
  setMembershipIntent: "SET_MEMBERSHIP_INTENT",
  setReserveIntent: "SET_RESERVE_INTENT",
  selectDates: "SELECT_DATES",
};

const ctaPropType = PropTypes.shape({
  ctaText: PropTypes.string,
  /**
   * Makes the CTA a link to a new page.
   * Either this or ctaAction must exist.
   */
  ctaUrlPath: PropTypes.string,
  /**
   * Sets a click action for the CTA.
   * SET_MEMBERSHIP_INTENT and SET_RESERVE_INTENT change the intent on the page.
   * SELECT_DATES triggers the reserve date picker.
   * Either this or ctaUrlPath must exist.
   */
  ctaAction: PropTypes.oneOf(Object.values(GridStickyCta.ctaActions)),
  /**
   * Used to indicate what intent (MEMBERSHIP or RESERVE) the CTA corresponds to, for pixel logging.
   * I.e. a link to the memberships LP would have MEMBERSHIP as its analytics intent.
   */
  ctaAnalyticsIntent: PropTypes.oneOf(Object.values(RENTAL_INTENT)),
});

const contentPropType = PropTypes.shape({
  primary: ctaPropType.isRequired,
  secondary: ctaPropType,
});

GridStickyCta.propTypes = {
  content: PropTypes.shape({
    [RENTAL_INTENT.NO_INTENT]: contentPropType,
    [RENTAL_INTENT.MEMBERSHIP]: contentPropType,
    [RENTAL_INTENT.RESERVE]: contentPropType,
  }),
  currentIntent: PropTypes.oneOf(Object.values(RENTAL_INTENT)),
  isActiveMembership: PropTypes.bool,
  isRentableGrid: PropTypes.bool,
  isVisible: PropTypes.bool,
  workingFilters: PropTypes.shape({
    date: PropTypes.string,
    duration: PropTypes.string,
  }),
};

export { GridStickyCta };
export default connect(mapStateToProps)(GridStickyCta);
